import { Search, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { PLATFORMS } from "../constants";
import ContentCard from "./ContentCard";

import TopSection from "./TopSection";

export default function HomePage({
  content,
  activeTab,
  onTabChange,
  filters,
  onFiltersChange,
  loading,
  stats,
  onCardClick,
  onEdit,
  onDelete,
  onMarkWatched,
  onMarkPending,
  onAdd,
  onCalendar,
}) {
  const [localSearch, setLocalSearch] = useState(filters.search || "");
  const debounceRef = useRef(null);

  // Debounce search
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onFiltersChange({ ...filters, search: localSearch });
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [localSearch]);

  // Get unique platforms from content for filter
  const usedPlatforms = [
    ...new Set(content.map((i) => i.platform).filter(Boolean)),
  ];

  return (
    <main className="max-w-7xl mx-auto px-3 sm:px-4 py-4">
      {/* Barra superior: Añadir, Buscar, Filtros, Top y Calendario */}
      <div className="flex flex-col gap-2 mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-steel-400"
            />
            <input
              type="text"
              placeholder="Buscar por nombre..."
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
              className="w-full bg-cinema-card border border-cinema-border rounded-xl pl-9 pr-9 py-2.5 text-white text-sm placeholder-steel-500 focus:outline-none focus:border-cinema-accent transition-all"
            />
            {localSearch && (
              <button
                onClick={() => setLocalSearch("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-steel-400 hover:text-white"
              >
                <X size={14} />
              </button>
            )}
          </div>
          <select
            value={filters.platform}
            onChange={(e) =>
              onFiltersChange({ ...filters, platform: e.target.value })
            }
            className="bg-cinema-card border border-cinema-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-cinema-accent transition-all appearance-none cursor-pointer min-w-[160px]"
          >
            <option value="">Todas las plataformas</option>
            {PLATFORMS.filter((p) => usedPlatforms.includes(p)).map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
          <select
            value={filters.type}
            onChange={(e) =>
              onFiltersChange({ ...filters, type: e.target.value })
            }
            className="bg-cinema-card border border-cinema-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-cinema-accent transition-all appearance-none cursor-pointer"
          >
            <option value="">Todo</option>
            <option value="movie">🎬 Películas</option>
            <option value="series">📺 Series</option>
          </select>
          <button
            className="flex items-center gap-2 bg-steel-600 hover:bg-steel-400 text-white font-medium px-4 py-2.5 rounded-xl transition-all duration-200 shadow-lg shadow-steel-700/20 text-sm"
            onClick={onCalendar}
          >
            <span>📅 Calendario</span>
          </button>
        </div>
        <TopSection onCardClick={onCardClick} />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 bg-cinema-card rounded-xl p-1">
        <button
          onClick={() => onTabChange("pending")}
          className={`flex-1 py-2.5 px-3 rounded-lg font-medium text-sm transition-all ${activeTab === "pending" ? "bg-steel-600 text-white shadow-md" : "text-steel-400 hover:text-white"}`}
        >
          📋 Pendientes ({stats.pending})
        </button>
        <button
          onClick={() => onTabChange("watched")}
          className={`flex-1 py-2.5 px-3 rounded-lg font-medium text-sm transition-all ${activeTab === "watched" ? "bg-emerald-600 text-white shadow-md" : "text-steel-400 hover:text-white"}`}
        >
          ✅ Vistas ({stats.watched})
        </button>
      </div>

      {/* Content Grid */}
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-cinema-card rounded-xl overflow-hidden">
              <div className="aspect-[2/3] shimmer" />
              <div className="p-3 space-y-2">
                <div className="h-4 shimmer rounded w-3/4" />
                <div className="h-3 shimmer rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : content.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {content.map((item) => (
            <ContentCard
              key={item.id}
              item={item}
              isWatched={activeTab === "watched"}
              onCardClick={onCardClick}
              onEdit={onEdit}
              onDelete={onDelete}
              onMarkWatched={onMarkWatched}
              onMarkPending={onMarkPending}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-16 animate-fade-in">
          <div className="text-5xl mb-4">
            {activeTab === "pending" ? "🎬" : "✅"}
          </div>
          <h3 className="text-lg font-semibold text-white mb-2">
            {activeTab === "pending"
              ? "Sin contenido pendiente"
              : "Nada visto aún"}
          </h3>
          <p className="text-steel-400 text-sm mb-4">
            {activeTab === "pending"
              ? "Añade películas y series para empezar"
              : "Marca contenido como visto"}
          </p>
          {activeTab === "pending" && (
            <button
              onClick={onAdd}
              className="bg-cinema-accent hover:bg-cinema-accent-hover text-white font-medium px-5 py-2.5 rounded-xl transition-all text-sm"
            >
              Añadir tu primer título
            </button>
          )}
        </div>
      )}
    </main>
  );
}
